import PropTypes from "prop-types";
import React from "react";

import { getOverHeadTransitionLabelOverrides } from "../../../../helpers";
import useOutputReportStore from "../../../../store/OutputReport.store";
import { getOutputType } from "../../../../utils/helpers/dataTable";
import BarChart from "../../../Chart/Bar";

import {
  createChartLegend,
  createTransitionScenario,
  getChartDataset,
  getChartOptions,
  getColorSequence,
  getDefaultBarChartLabels,
  getEquityBarChartLabels,
  getXlabels,
  getYlabels,
} from "../../../../utils/helpers/chart";

const SectorsChart = ({ reportName, id, isFullWidth, name }) => {
  const data = useOutputReportStore((state) => state.data);
  const { reportType, assetType } = getOutputType(data);

  const sectorConfig = {
    transition: {
      hasTransitionScenario: true,
    },
    cash: {
      hasTransitionScenario: false,
    },
  };

  const fullWidthHeight = assetType === "corp" ? "680px" : "480px";

  const reportData = data[reportName];

  const yLabels = getYlabels(reportData, ["Total"]);
  const { xLabels, xLabelOverrides } = getXlabels(
    reportData,
    [],
    assetType,
    reportType,
    name || reportName
  );

  const barChartId = id;

  // Stacked Bar Options
  const config = {
    isFullWidth,
    isLegend: true,
    tooltipValueType: "percent-x-hundred-fixed",
    yTicksType: "percent-x-hundred",
    yStepSize: 0.5,
    isStacked: true,
    hasYannotation: false,
    ...sectorConfig[reportType],
  };
  const chartOptions = getChartOptions(config);

  // Stacked Bar Styles
  const chartStyles = getColorSequence();

  // Stacked Bar Data - Y Axis Data
  const chartDatasets = getChartDataset(reportData, yLabels, xLabels);

  // X Labels - X Axis Data
  const chartLabels = {
    transition:
      assetType === "equity"
        ? getEquityBarChartLabels()
        : getDefaultBarChartLabels(),
    cash: xLabelOverrides,
  };

  // Format Chart Data
  const chartData = {
    labels: chartLabels[reportType],
    datasets: chartDatasets
      .map((item, idx) => {
        return {
          ...item,
          ...chartStyles[idx],
        };
      })
      .reverse(),
  };

  let legendTitle = name === "sectors-gics-ose" ? "Sectors[3]" : "Sectors[1]";

  if (
    (reportType === "transition" && assetType === "corp") ||
    (reportType === "cash" && assetType === "muni") ||
    (reportType === "cash" && assetType === "corp")
  ) {
    legendTitle = "Sectors";
  }

  // Legend for Sectors
  const chartLegend = createChartLegend({
    containerId: barChartId,
    legendId: `legend-${barChartId}`,
    legendTitle,
    isReversed: true,
    isFullWidth,
  });

  const scenarioText = getOverHeadTransitionLabelOverrides(
    assetType,
    reportType,
    reportName
  );

  // Label for Transition Scenarios
  const transitionChartId = `transition-scenario-${reportType}`;
  const transitionScenario = createTransitionScenario(
    transitionChartId,
    barChartId,
    scenarioText
  );

  return (
    <BarChart
      id={barChartId}
      data={chartData}
      options={chartOptions}
      plugins={[
        config.isLegend ? chartLegend : {},
        config.hasTransitionScenario ? transitionScenario : {},
      ]}
      ariaLabel="Sectors Report Bar Chart"
      height={isFullWidth ? fullWidthHeight : "372px"}
    />
  );
};

SectorsChart.propTypes = {
  reportName: PropTypes.string,
  id: PropTypes.string,
  isFullWidth: PropTypes.bool,
};

SectorsChart.defaultProps = {
  id: "bar-chart-sectors",
  isFullWidth: true,
  reportName: "Sector",
};

export default SectorsChart;
